/**
 * PrefetchWarmer — warms the React Query cache with the most-visited
 * data as soon as the user is authenticated.
 *
 * Rationale: the user is staring at the Dashboard for a few seconds while
 * reading their weight/streak. During that idle time we asynchronously load
 * data they're likely to request next (premium, challenges, feed banner).
 * The end effect is that opening Profile / Feed / Challenges feels instant.
 *
 * Deliberately small and side-effect free — no rendering, no re-renders.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';

const PREFETCH_DELAY_MS = 800; // let the current screen settle first

export default function PrefetchWarmer() {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const timer = setTimeout(() => {
      // Kick off in parallel — React Query will deduplicate if another
      // component fires the same query in the meantime.
      qc.prefetchQuery({
        queryKey: ['premium', 'status'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/premium/status`);
          return res.data || {};
        },
        staleTime: 60_000,
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ['ad-banner', 'feed'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/ads/banner/feed`);
          return res.data || null;
        },
        staleTime: 5 * 60_000,
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ['ad-banner', 'home'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/ads/banner/home`);
          return res.data || null;
        },
        staleTime: 5 * 60_000,
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ['dashboard-buttons'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/dashboard-buttons`);
          return res.data?.buttons || null;
        },
        staleTime: 5 * 60_000,
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/notifications/unread-count`);
          return res.data || { count: 0 };
        },
        staleTime: 15_000,
      }).catch(() => {});

      // Challenges — the two lists are visible as soon as the user taps "Sfide"
      // from the bottom nav, so warm them together.
      qc.prefetchQuery({
        queryKey: ['challenges', 'mine'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/challenges`);
          return res.data || [];
        },
        staleTime: 30_000,
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ['challenges', 'public'],
        queryFn: async () => {
          const res = await httpClient.get(`${API_URL}/api/challenges/public`);
          return res.data || [];
        },
        staleTime: 60_000,
      }).catch(() => {});

      // Feed 'all' first page — warms the useInfinitePosts cache so that
      // tapping "Feed" in the bottom nav shows posts without any spinner.
      qc.prefetchInfiniteQuery({
        queryKey: ['posts', 'feed', 'all'],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
          const res = await httpClient.get(
            `${API_URL}/api/posts?page=${pageParam}&limit=10&filter=all`
          );
          const data = res.data || {};
          return {
            posts: data.posts || [],
            has_more: !!data.has_more,
            page: data.page || pageParam,
          };
        },
        staleTime: 30_000,
      }).catch(() => {});
    }, PREFETCH_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user, qc]);

  return null;
}
